import { injectable } from 'inversify';
import type { IPlanRepository, PlanFormData } from '@/domain/repositories/IPlanRepository';
import { Plan } from '@/domain/entities/Plan';
import { httpClient } from '@/infrastructure/http/client/httpClient';

@injectable()
export class ApiPlanRepository implements IPlanRepository {
  async getAll(): Promise<Plan[]> {
    try {
      const response = await httpClient.get<any[]>('/plans');
      return response.data.map(item => 
        Plan.create(
          item.id,
          item.nombre,
          item.consultorios,
          item.profesionales,
          item.precio,
          item.planActivo,
          item.fechaAlta ? new Date(item.fechaAlta) : new Date(),
          item.fechaModificacion ? new Date(item.fechaModificacion) : new Date()
        )
      );
    } catch (error) {
      console.error('Error fetching plans:', error);
      throw error;
    }
  }

  async getById(id: number): Promise<Plan> {
    try {
      const response = await httpClient.get<any>(`/plans/${id}`);
      const data = response.data;
      
      return Plan.create(
        data.id,
        data.nombre,
        data.consultorios,
        data.profesionales,
        data.precio,
        data.planActivo,
        data.fechaAlta ? new Date(data.fechaAlta) : new Date(),
        data.fechaModificacion ? new Date(data.fechaModificacion) : new Date()
      );
    } catch (error) {
      console.error('Error fetching plan:', error);
      throw error;
    }
  }

  async create(data: PlanFormData): Promise<Plan> {
    try {
      const response = await httpClient.post<any>('/plans', {
        nombre: data.nombre,
        consultorios: data.consultorios,
        profesionales: data.profesionales,
        precio: data.precio,
        precioUsd: data.precioUsd,
      });

      const planData = response.data;
      return Plan.create(
        planData.id,
        planData.nombre,
        planData.consultorios,
        planData.profesionales,
        planData.precio,
        planData.planActivo,
        planData.fechaAlta ? new Date(planData.fechaAlta) : new Date(),
        planData.fechaModificacion ? new Date(planData.fechaModificacion) : new Date()
      );
    } catch (error) {
      console.error('Error creating plan:', error);
      throw error;
    }
  }

  async update(id: number, data: PlanFormData): Promise<Plan> {
    try {
      const response = await httpClient.put<any>(`/plans/${id}`, {
        nombre: data.nombre,
        consultorios: data.consultorios,
        profesionales: data.profesionales,
        precio: data.precio,
        precioUsd: data.precioUsd,
      });

      const planData = response.data;
      return Plan.create(
        planData.id,
        planData.nombre,
        planData.consultorios,
        planData.profesionales,
        planData.precio,
        planData.planActivo,
        planData.fechaAlta ? new Date(planData.fechaAlta) : new Date(),
        planData.fechaModificacion ? new Date(planData.fechaModificacion) : new Date()
      );
    } catch (error) {
      console.error('Error updating plan:', error);
      throw error;
    }
  }

  async toggleStatus(id: number): Promise<void> {
    try {
      await httpClient.patch(`/plans/${id}/status`);
    } catch (error) {
      console.error('Error toggling plan status:', error);
      throw error;
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await httpClient.delete(`/plans/${id}`);
    } catch (error: unknown) {
      console.error('Error deleting plan:', error);
      const axiosErr = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const msg =
        axiosErr.response?.data?.message ||
        (error instanceof Error ? error.message : 'Error al eliminar el plan');
      throw new Error(msg);
    }
  }
}
